/**
 * Hook del listado de Membresías con paginación server-side (US-2.4).
 *
 * La búsqueda por nombre (`q`) se debounce antes de golpear el backend: el input
 * actualiza los filtros de inmediato y la petición sale tras la pausa de escritura.
 * La red vive en `membershipApi`; aquí solo el estado de la vista.
 */
import { useCallback, useEffect, useState } from 'react';
import type {
  ListMembershipsQuery,
  Membership,
  PageMeta,
} from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { membershipApi, type MembershipListParams } from '../api/membershipApi';

/** Límite por página por defecto del Admin Web. */
export const DEFAULT_MEMBERSHIPS_LIMIT = 20;

/** Pausa de la búsqueda antes de consultar el backend. */
export const MEMBERSHIPS_SEARCH_DEBOUNCE_MS = 300;

/**
 * Filtros del listado (los del contrato, sin paginación).
 *
 * `status` incluye `ALL` para que el Admin vea también las membresías dadas de baja
 * (`INACTIVE`); el default del backend (`ACTIVE`) aplica solo si se omite el filtro.
 */
export type MembershipFilters = Omit<ListMembershipsQuery, 'offset' | 'limit'>;

/** Alcance de estados permitido para el rol que consulta. */
export type MembershipStatusScope = 'all' | 'active-only';

export interface UseMembershipsOptions {
  /** Elementos por página (default `DEFAULT_MEMBERSHIPS_LIMIT`). */
  limit?: number;
  /**
   * `all` (roles de plataforma) permite `ALL` y todos los estados; `active-only`
   * fija el backend en `ACTIVE` y oculta el filtro (ALIADO_ADMIN/CONTADOR).
   */
  statusScope?: MembershipStatusScope;
  /** Filtros iniciales; por defecto `ALL` (o `ACTIVE` en `active-only`). */
  initialFilters?: MembershipFilters;
}

export interface UseMembershipsResult {
  data: Membership[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  /** Filtros activos (la `q` se aplica al backend con debounce). */
  filters: MembershipFilters;
  /** Desplazamiento actual (paginación server-side). */
  offset: number;
  limit: number;
  /** Aplica filtros nuevos y reinicia la paginación a la primera página. */
  setFilters: (filters: MembershipFilters) => void;
  /** Mueve la página (offset) actual. */
  setOffset: (offset: number) => void;
  /** Recarga la página actual (p. ej. tras editar o dar de baja una membresía). */
  refresh: () => void;
}

const emptyMeta = (limit: number): PageMeta => ({
  offset: 0,
  limit,
  total: 0,
  hasMore: false,
});

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudieron cargar las membresías.';
}

/** Retrasa la propagación de un valor (para no consultar en cada tecla). */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(handle);
  }, [value, delay]);

  return debounced;
}

export function useMemberships(
  options: UseMembershipsOptions = {},
): UseMembershipsResult {
  const limit = options.limit ?? DEFAULT_MEMBERSHIPS_LIMIT;
  const statusScope = options.statusScope ?? 'all';
  const [filters, setFiltersState] = useState<MembershipFilters>(() => ({
    ...options.initialFilters,
    status:
      statusScope === 'active-only'
        ? 'ACTIVE'
        : (options.initialFilters?.status ?? 'ALL'),
  }));
  const [offset, setOffsetState] = useState(0);
  const [data, setData] = useState<UseMembershipsResult['data']>([]);
  const [meta, setMeta] = useState<PageMeta>(() => emptyMeta(limit));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const rawQuery = filters.q?.trim() ?? '';
  const debouncedQuery = useDebouncedValue(rawQuery, MEMBERSHIPS_SEARCH_DEBOUNCE_MS);
  // En `active-only` el estado se fuerza a ACTIVE aunque un caller intente otro.
  const effectiveStatus = statusScope === 'active-only' ? 'ACTIVE' : filters.status;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params: MembershipListParams = {
      offset,
      limit,
      ...(effectiveStatus !== undefined ? { status: effectiveStatus } : {}),
      ...(debouncedQuery ? { q: debouncedQuery } : {}),
    };

    membershipApi
      .list(params)
      .then((page) => {
        if (cancelled) return;
        setData(page.data);
        setMeta(page.meta);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(toErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Descarta respuestas de una página/filtro anterior que lleguen fuera de orden.
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, effectiveStatus, offset, limit, reloadKey]);

  const setFilters = useCallback(
    (next: MembershipFilters) => {
      setOffsetState(0);
      setFiltersState({
        ...next,
        status:
          statusScope === 'active-only' ? 'ACTIVE' : (next.status ?? 'ALL'),
      });
    },
    [statusScope],
  );

  const setOffset = useCallback((next: number) => {
    setOffsetState(next < 0 ? 0 : next);
  }, []);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return {
    data,
    meta,
    loading,
    error,
    filters,
    offset,
    limit,
    setFilters,
    setOffset,
    refresh,
  };
}
